import React, { ComponentProps, ReactNode, useState, useEffect, useRef } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "../../../../constants/icons";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./styles.css";
import { Heading } from "../../../../components/UI";

type TCarouselProps = {
  children: ReactNode;
  autoPlay?: boolean;
  interval?: number;
  label?: string;
  labelClassName?: string;
  numberClassName?: string;
  containerClassName?: string;
};

export default function Carousel({
  children,
  autoPlay = true,
  interval = 10000,
  label,
  labelClassName = "",
  numberClassName = "",
  containerClassName = "",
}: TCarouselProps) {
  const [activeItem, setActiveItem] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const items = React.Children.toArray(children);
  const totalItems = items.length;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isInViewPort, setIsInViewPort] = useState(false);

  const [wrapperHeight, setWrapperHeight] = useState<number | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const updateWrapperHeight = () => {
    if (wrapperRef.current) {
      const activeSlide = wrapperRef.current.querySelector(".landing-page-custom-carousel-item");
      if (activeSlide) {
        setWrapperHeight(activeSlide.clientHeight || 300); // Set min height
      }
    }
  };

  // Update height on first mount
  useEffect(() => {
    setTimeout(updateWrapperHeight, 300); // Delay ensures content is rendered
  }, []);

  useEffect(() => {
    updateWrapperHeight();
  }, [activeItem]);

  useEffect(() => {
    if (!autoPlay && !isInViewPort) return;
    const timer = setInterval(() => {
      handleRightIconClick();
    }, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, isInViewPort]);

  const handleLeftIconClick = () => {
    setDirection("left");
    setActiveItem((prev) => (prev === 0 ? totalItems - 1 : prev - 1));
  };

  const handleRightIconClick = () => {
    setDirection("right");
    setActiveItem((prev) => (prev === totalItems - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if(containerRef.current == null) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsInViewPort(entry.isIntersecting);
    });    

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [])

  return (
    <div ref={containerRef} className={`flex-col ${containerClassName}`}>
      <div className="flex-between">
        <div></div>
        {label && <Heading className={labelClassName}>{label}</Heading>}
        <p className={`${numberClassName} fw-500`}>{activeItem + 1}/{items.length}</p>
      </div>
      <div className="landing-page-custom-carousel-container relative" role="region" aria-label="Carousel">
        <Arrow style={{ left: 0, top: 0, bottom: 0, zIndex: 1 }} onClick={handleLeftIconClick} aria-label="Previous Slide">
          <ArrowLeftIcon />
        </Arrow>

        <div
          className="landing-page-custom-carousel-wrapper"
          aria-live="polite"
          ref={wrapperRef}
          style={{
            minHeight: wrapperHeight || 300,
            height: wrapperHeight || "auto",
          }}
        >
          <TransitionGroup component={null}>
            <CSSTransition
              key={activeItem}
              timeout={400}
              classNames={`landing-page-custom-carousel-slide-${direction}`}
              onEntered={updateWrapperHeight} // Update height after animation
            >
              <div className="landing-page-custom-carousel-item">{items[activeItem]}</div>
            </CSSTransition>
          </TransitionGroup>
        </div>

        <Arrow style={{ right: 0, top: 0, bottom: 0, zIndex: 1 }} onClick={handleRightIconClick} aria-label="Next Slide">
          <ArrowRightIcon />
        </Arrow>
      </div>
    </div>
  );
}

type TArrowProps = {
  children: ReactNode;
} & ComponentProps<"div">;

function Arrow({ children, ...props }: TArrowProps) {
  return (
    <div role="button" className="flex-center absolute" onClick={props.onClick} {...props}>
      <button className="landing-page-custom-carousel-arrow">
        {children}
      </button>
    </div>
  );
}
